import React, {
  createContext,
  useContext,
  useMemo,
  ReactNode,
  FC,
} from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { apollo, setContext, onError } from "..";
import { Subscription } from "zen-observable-ts";
import { Env, getEnv } from "../../utils/getEnv";
import {
  deleteLoginCookies,
  getAccessToken,
  getRefreshToken,
  getUserLookupData,
  setLoginCookies,
} from "../../utils/cookie";
import { useAlerts } from "../../contexts/alert/AlertsContext";
import { AlertTypes, PushAlertType } from "../../contexts/alert/types";
import { RoutesPage } from "../../constants/routes";
import { ONE_IO_API } from "./authnClientProvider";
import {
  AuthRefreshTokenMutationResponse,
  AuthRefreshTokenMutationVariables,
} from "../../types/user";

const UPS_API: Record<Env, string> = {
  local: "http://localhost:8081",
  dev: "https://user.api.dev.carrier.io/graphql",
  qa: "https://user.api.qa.carrier.io/graphql",
  uat: "https://user.api.preprod.carrier.io/graphql",
  prod: "https://user.api.preprod.carrier.io/graphql",
};

const fetchNewAccessToken = async ({
  refreshToken,
  region,
}: AuthRefreshTokenMutationVariables): Promise<AuthRefreshTokenMutationResponse | null> => {
  try {
    const res = await fetch(ONE_IO_API[getEnv(window.location.hostname)], {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: `
          mutation AuthRefreshToken($refreshToken: String!, $region: AWSRegions!) {
            authRefreshToken(refreshToken: $refreshToken, region: $region) {
              idToken
              accessToken
              refreshToken
            }
          }
        `,
        variables: {
          refreshToken,
          region,
        },
      }),
    });
    const data = await res.json();

    return data?.data ?? null;
  } catch (err) {
    console.error("Failed to refresh token:", err);
    return null;
  }
};

const refreshToken = getRefreshToken() || "";
const accessToken = getAccessToken();
const { region = "" } = getUserLookupData();

const updateToken = async () => {
  try {
    if (!refreshToken || !region) {
      return null;
    }

    const { authRefreshToken } =
      (await fetchNewAccessToken({ refreshToken, region })) ?? {};

    if (authRefreshToken) {
      setLoginCookies(
        {
          idToken: authRefreshToken?.idToken,
          accessToken: authRefreshToken?.accessToken,
          refreshToken: authRefreshToken?.refreshToken,
        },
        window.location.hostname,
      );

      return authRefreshToken?.accessToken ?? null;
    }

    return null;
  } catch (err) {
    console.log("Auth Refresh Token Error: ", err);
    return null;
  }
};

const createUpsClient = (
  navigate: NavigateFunction,
  pushAlert: PushAlertType,
) => {
  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors) {
        for (const err of graphQLErrors || []) {
          if (err.extensions?.code === "FORBIDDEN") {
            pushAlert(
              AlertTypes.ERROR,
              "User is not authorized to access this resource",
            );
            navigate(RoutesPage.Dashboard);
            return;
          } else if (err.extensions?.code === "UNAUTHORIZED") {
            return apollo
              .fromPromise(
                updateToken().then((newToken) => {
                  if (newToken) {
                    setAuthorizationHeader(operation, newToken);
                    return forward(operation);
                  }
                  pushAlert(
                    AlertTypes.ERROR,
                    "Session expired. Please log in again.",
                  );
                  deleteLoginCookies(window.location.hostname);
                  navigate(RoutesPage.Login);
                  throw new Error("Failed to refresh token");
                }),
              )
              .flatMap((observable) => observable);
          }
        }
      }

      if (networkError && "statusCode" in networkError) {
        const { statusCode } = networkError as never;
        if (statusCode === 403) {
          pushAlert(
            AlertTypes.ERROR,
            "User is not authorized to access this resource",
          );
          navigate(RoutesPage.Dashboard);
        }
      }
    },
  );

  const authLink = setContext((_, { headers }) => {
    const token = getAccessToken();
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
      },
    };
  });

  const setAuthorizationHeader = (
    operation: apollo.Operation,
    token: string,
  ) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        Authorization: `Bearer ${token}`,
      },
    }));
  };

  const refreshLink = new apollo.ApolloLink(
    (operation: apollo.Operation, forward) => {
      return new apollo.Observable<apollo.FetchResult>((observer) => {
        let sub: Subscription;

        const handleError = async (error) => {
          let newToken: string | null = null;

          if (refreshToken && !accessToken) {
            newToken = await updateToken();
          }

          if (!newToken) {
            observer.error(error);
            return;
          }

          setAuthorizationHeader(operation, newToken);

          sub = forward(operation).subscribe(observer);
        };

        sub = forward(operation).subscribe({
          next: (value) => observer.next(value),
          error: handleError,
          complete: () => observer.complete(),
        });

        return () => sub?.unsubscribe?.();
      });
    },
  );

  return new apollo.ApolloClient({
    link: apollo.from([
      refreshLink,
      errorLink,
      authLink,
      new apollo.HttpLink({ uri: UPS_API[getEnv(window.location.hostname)] }),
    ]),
    cache: new apollo.InMemoryCache({
      typePolicies: {
        ExtendedUserResponse: {
          keyFields: ["email"],
        },
      },
    }),
  });
};

const UPSContext = createContext({} as ReturnType<typeof createUpsClient>);

export const useUPSClient = () => useContext(UPSContext);

export const UPSClientProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { pushAlert } = useAlerts();
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const upsClient = useMemo(() => createUpsClient(navigate, pushAlert), []);

  return (
    <UPSContext.Provider value={upsClient}>
      <apollo.ApolloProvider client={upsClient}>
        {children}
      </apollo.ApolloProvider>
    </UPSContext.Provider>
  );
};
